import { Component, ElementRef, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';

import { 
    ControlValueAccessor, 
    NG_VALUE_ACCESSOR, 
    Validator, 
    NG_VALIDATORS 
} from '@angular/forms';
import { DataFilter, DataService, PgFilterStatus } from '../../services/data.service';
import { PgFile, PgFileType, PgFileUtils } from '../../models/file.model';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { PgImageCropModalComponent } from '../../pg-ui-elements/pg-image-crop-modal/pg-image-crop-modal.component';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../services/config.service';
import { OptionMapsService } from '../../services/option-maps.service';

class PgFilePickerSelected {
    progress?:number;
    error?:string;

    file?:File;
    external?:string;

    constructor(fromData: File|string) {
        if(typeof fromData == 'string') {
            this.external = fromData
        }
        else {
            this.file = fromData
        }
    }
}

@Component({
    selector: 'app-pg-file-picker',
    templateUrl: './pg-file-picker.component.html',
    styleUrls: ['./pg-file-picker.component.scss'],
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PgFilePickerComponent),
            multi: true
        }, {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => PgFilePickerComponent),
            multi: true,
        }],
    standalone: false
})
export class PgFilePickerComponent implements ControlValueAccessor, Validator, OnInit, OnChanges {

    constructor(private dataService:DataService, private modalService:NgbModal, private configService: ConfigService, private optionMapsService:OptionMapsService) { }

    ngOnInit(): void {}

    @Input() fieldId:string;

    @Input() readonly: boolean;

    @Input() required: boolean;

    @Input() multi: boolean;

    @Input() fileType:PgFileType;
    @Input() fileOptions:any;

    @Input() groupId:string;
    @Input() realmId:string;
    @Input() directoryId:string;

    @Input() extraActions:Array<{
        name:string,
        color:string,
        icon:string
    }>;

    @Input() forceRealm:string;

    @Output() onExtraAction = new EventEmitter<{ action: string, file: string }>()

    acceptString:string = null;

    autoConfirm = true;

    resourcePickerFilterStatus:PgFilterStatus = null;

    canSelectFile = false;

    ngOnChanges() {
        if(this.fileOptions != null && this.fileOptions.acceptString != null) {
            this.acceptString = this.fileOptions.acceptString;
        }
        else {
            this.acceptString = null;
        }

        this.resourcePickerFilterStatus = new PgFilterStatus()

        if(this.fileType != null) {
            this.resourcePickerFilterStatus.filter = [{
                field: 'type',
                operator: '==',
                value: [ this.fileType ]
            }]
        }

        this.canSelectFile = this.configService.getResourceActions('File')?.list;
    }

    isLoading = false;

    alreadyLoaded:Array<PgFile> = null;
    selectedFiles:Array<PgFilePickerSelected> = null;

    value:File|Array<File> = null;

    setFiles(files:Array<File|string>) {

        if(this.selectedFiles == null || !this.multi) {
            this.selectedFiles = [];
        }
        
        for(let i = 0; i < files.length; i++) {
            let _file = files[i];

            if(typeof _file == 'string') {
                this.selectedFiles.push({ external: _file })
            }
            else {
                this.selectedFiles.push({ file: _file })
            }
        }

        if(this._onTouched != null) this._onTouched();

        if(this.autoConfirm) this.saveFiles();
    }

    hasChanges() {
        for(let _cLoaded of this.alreadyLoaded) {
            if(_cLoaded.deleted) return true;
        }

        return this.selectedFiles != null && this.selectedFiles.length > 0;
    }

    getFileCategoryIcon(nullDefault?:boolean) {
        return PgFileUtils.getCategoryIcon(this.fileType, nullDefault)
    }

    toggleAlreadyUploadedDelete(file:PgFile) {
        file.deleted = !file.deleted;
        if(this._onTouched != null) this._onTouched();

        if(this.autoConfirm) this.saveFiles();
    }

    removeSelectedFile(file:PgFilePickerSelected) {
        this.selectedFiles.splice(this.selectedFiles.indexOf(file), 1);
    }

    private _modalImageCropRef:NgbModalRef = null;

    modalImageCropTarget:PgFilePickerSelected = null;

    openImageCropModal(file:PgFilePickerSelected) { 
        this.modalImageCropTarget = file;

        this._modalImageCropRef = this.modalService.open(PgImageCropModalComponent);
        let _cComponentInstance = this._modalImageCropRef.componentInstance as PgImageCropModalComponent;

        let _cObjectURL = URL.createObjectURL(file.file);
        _cComponentInstance.imageURL = _cObjectURL;

        if(this.fileOptions != null && this.fileOptions.imageRatio != null) {
            _cComponentInstance.cropperSettings.width = this.fileOptions.imageRatio.width;
            _cComponentInstance.cropperSettings.height = this.fileOptions.imageRatio.height;
            _cComponentInstance.cropperSettings.keepAspect = true;
        }
        
        this._modalImageCropRef.result.then((data) => {
            URL.revokeObjectURL(_cObjectURL)

            var byteString = atob(data.split(',')[1]);

            var ab = new ArrayBuffer(byteString.length);
            var ia = new Uint8Array(ab);
            for (var i = 0; i < byteString.length; i++) {
                ia[i] = byteString.charCodeAt(i);
            }

            this.modalImageCropTarget.file = new File([ab], this.modalImageCropTarget.file.name)
        },() => {
            URL.revokeObjectURL(_cObjectURL)
        })
    }

    resourcePickerSelection:Array<string> = null;

    onResourcePickerSelectionChange() {
        if(this._onTouched != null) this._onTouched();

        this.alreadyLoaded = [];
        this.loadFiles(this.resourcePickerSelection, true)
    }

    hasEditFile() {
        return !this.readonly
    }

    modalEditTargetId:string = null;
    modalEditTargetName:string = null;
    modalEditFormData:any = null;

    @ViewChild('modalEdit') modalEdit:ElementRef;

    private _modalEditRef:NgbModalRef = null;

    licenseSelectOptions:Array<{ value: string, text: string, data?:any }> = null;

    editFile(file:PgFile) {
        // questo potrebbe essere fatto all'inizio
        this.licenseSelectOptions = this.optionMapsService.getResourceOptionMaps('File')['license']; 

        this.modalEditTargetId = file.id;
        this.modalEditTargetName = file.nome;

        this.modalEditFormData = {... file}

        this.modalEditFormData.license_other = null

        if(this.modalEditFormData.license != null) {
            let _isIn = false;

            for(let _option of this.licenseSelectOptions) {
                if(_option.value == this.modalEditFormData.license) {
                    _isIn = true;
                    break;
                }
            }

            if(!_isIn) {
                this.modalEditFormData.license_other = this.modalEditFormData.license
                this.modalEditFormData.license = 'other'
            }
        }

        this._modalEditRef = this.modalService.open(this.modalEdit)
    }

    modalEditCancel() {
        this._modalEditRef.dismiss();
    }

    modalEditSubmit() {
        let _values = JSON.parse(JSON.stringify(this.modalEditFormData))

        if(_values.license == 'other') {
            _values.license = _values.license_other;
        }

        if(_values.license_other != null) {
            delete _values.license_other;
        }

        for(let i = 0; i < this.alreadyLoaded.length; i++) {
            if(this.alreadyLoaded[i].id == this.modalEditTargetId) {
                
                this.isSaving = true

                this.dataService.putElementData('File', this.modalEditTargetId, _values).subscribe((data) => {
                    this.isSaving = false

                    this.alreadyLoaded[i] = new PgFile(data)
                })
                break;
            }
        }

        this._modalEditRef.dismiss();
    }

    modalLinkValue:string = null;
    modalLinkFile:File = null;
    modalLinkFileName:string = null;

    modalLinkPreviewStatus = {
        load: false,
        success: false,
        error: false
    }

    private _modalLinkPreviewStatusReset() {
        for(let i in this.modalLinkPreviewStatus) {
            this.modalLinkPreviewStatus[i] = false;
        }
    }

    @ViewChild('modalLink') modalLink:ElementRef;
    @ViewChild('modalLinkInput') modalLinkInput:ElementRef;

    private _modalLinkRef:NgbModalRef = null;

    modalLinkOpen() {
        this.modalLinkValue = null;
        this.modalLinkFile = null;
        this.modalLinkFileName = null;
        this._modalLinkExtraFiles = [];
        this._modalLinkPreviewStatusReset()

        this._modalLinkRef = this.modalService.open(this.modalLink)

        this._modalLinkRef.shown.subscribe(() => {
            this.modalLinkInput?.nativeElement?.focus();
        })
    }

    modalLinkCancel() {
        this._modalLinkRef.dismiss();
    }

    modalLinkSubmit() {
        if(this.modalLinkPreviewStatus.success) {
            if(this.modalLinkFile) {
                let _file = this._applyModalLinkFileName(this.modalLinkFile, this.modalLinkFileName);
                this.setFiles([_file, ...this._modalLinkExtraFiles])
            }
            else if(this.modalLinkValue) {
                this.setFiles([this.modalLinkValue])
            }
    
            this._modalLinkRef.dismiss();
        }
    }

    modalLinkClearFile() {
        this.modalLinkFile = null;
        this.modalLinkFileName = null;
        this._modalLinkExtraFiles = [];
        this._modalLinkPreviewStatusReset();

        setTimeout(() => {
            this.modalLinkInput?.nativeElement?.focus();
        }, 0)
    }

    private _applyModalLinkFileName(file:File, fileName:string) {
        let _name = (fileName || '').trim();
        if(!_name || _name == file.name) return file;

        return new File([file], _name, { type: file.type || 'application/octet-stream' });
    }

    modalLinkNeedsPreview() {
        return this.fileType == 'images' || this.fileType == 'audios' || this.fileType == 'videos';
    }

    private _onModalLinkValueChangeTimeout = null;

    onModalLinkValueChange() {
        this.modalLinkFile = null;
        this._modalLinkPreviewStatusReset();

        if(this.modalLinkValue) {
            if(!this.modalLinkNeedsPreview()) {
                this.modalLinkPreviewStatus.success = true
            }
            else {
                clearTimeout(this._onModalLinkValueChangeTimeout)
    
                this._onModalLinkValueChangeTimeout = setTimeout(() => {
                    this.modalLinkPreviewStatus.load = true;
                }, 500)
            }
        }
    }

    onModalLinkPaste(event:ClipboardEvent) {
        let _files = this._getFilesFromClipboard(event.clipboardData);

        if(_files.length > 0) {
            event.preventDefault();
            event.stopPropagation();

            let _accepted = this._filterClipboardFiles(_files);

            if(_accepted.length == 0) {
                this.modalLinkValue = null;
                this.modalLinkFile = null;
                this.modalLinkFileName = null;
                this._modalLinkPreviewStatusReset();
                this.modalLinkPreviewStatus.error = true;
                return;
            }

            this.modalLinkValue = null;
            this.modalLinkFile = _accepted[0];
            this.modalLinkFileName = this.modalLinkFile.name;
            this._modalLinkPreviewStatusReset();

            if(!this.modalLinkNeedsPreview()) {
                this.modalLinkPreviewStatus.success = true;
            }
            else {
                this.modalLinkPreviewStatus.load = true;
            }

            if(this.multi && _accepted.length > 1) {
                // keep first in modal for confirm/preview; queue the rest on submit via selectedFiles
                this._modalLinkExtraFiles = _accepted.slice(1);
            }
            else {
                this._modalLinkExtraFiles = [];
            }
        }
    }

    private _modalLinkExtraFiles:Array<File> = [];

    private _getFilesFromClipboard(clipboardData:DataTransfer) {
        let _files:Array<File> = [];

        if(clipboardData == null) return _files;

        if(clipboardData.files != null && clipboardData.files.length > 0) {
            for(let i = 0; i < clipboardData.files.length; i++) {
                let _file = clipboardData.files.item(i);
                if(_file) _files.push(this._ensureClipboardFileName(_file));
            }
        }
        else if(clipboardData.items != null) {
            for(let i = 0; i < clipboardData.items.length; i++) {
                let _item = clipboardData.items[i];
                if(_item.kind == 'file') {
                    let _file = _item.getAsFile();
                    if(_file) _files.push(this._ensureClipboardFileName(_file));
                }
            }
        }

        return _files;
    }

    private _ensureClipboardFileName(file:File) {
        if(file.name && /\.[^\.]+$/.test(file.name)) return file;

        let _ext = 'bin';
        let _base = 'file';

        if(file.type) {
            let _parts = file.type.split('/');
            if(_parts.length == 2 && _parts[1]) {
                _ext = _parts[1].split('+')[0].split(';')[0];
                if(_ext == 'jpeg') _ext = 'jpg';
            }

            switch(_parts[0]) {
                case 'image': _base = 'image'; break;
                case 'video': _base = 'video'; break;
                case 'audio': _base = 'audio'; break;
            }
        }

        return new File([file], _base + '.' + _ext, { type: file.type || 'application/octet-stream' });
    }

    private _filterClipboardFiles(files:Array<File>) {
        let _accepted:Array<File> = [];

        for(let _file of files) {
            if(this._doesClipboardFileMatchType(_file)) {
                _accepted.push(_file);
            }
        }

        return _accepted;
    }

    private _doesClipboardFileMatchType(file:File) {
        if(this.fileType == null) return true;

        if(file.type) {
            let _mimeCategory = file.type.split('/')[0];
            if(this.fileType == 'images' && _mimeCategory == 'image') return true;
            if(this.fileType == 'videos' && _mimeCategory == 'video') return true;
            if(this.fileType == 'audios' && _mimeCategory == 'audio') return true;
        }

        return !!PgFileUtils.doesExtensionMatchType(this.fileType, file.name);
    }

    // INTERFACCIA ControlValueAccessor

    writeValue(obj:string) {
        this.alreadyLoaded = [];
        this.resourcePickerSelection = [];

        if(obj != null && typeof obj == 'string' && obj != '') {
            let _cValues = [];

            try {
                _cValues = JSON.parse(obj)
            }
            catch(ex) {
                _cValues = [obj]
            }

            for(let i = _cValues.length - 1; i >= 0; i--) {
                if(_cValues[i] == null) _cValues.splice(i, 1)
            }

            this.resourcePickerSelection = _cValues;
            this.loadFiles(_cValues)
        }
    }

    private _getFileNameFromUrl(url:string) {
        if(url != null) {
            let _fileNameMatch = url.match(/\/([^\/]*)$/)
            if(_fileNameMatch != null) {
                return _fileNameMatch[1];
            }
        }
    }

    private _loadFileSubscription:Subscription = null;

    loadFiles(urlList:Array<string>, andTriggerChange?:boolean) {
        this.isLoading = true;

        let _fileNameList:Array<string> = [];
        for(let _url of urlList) {
            let _fileName = this._getFileNameFromUrl(_url)
            if(_fileName != null) {
                _fileNameList.push(_fileName)
            }
        }

        if(this._loadFileSubscription != null) this._loadFileSubscription.unsubscribe();

        this._loadFileSubscription = this.dataService.getResourceData('File', {
            filter: [new DataFilter('nome_file', 'in', _fileNameList)],
            realm: this.forceRealm
        }).subscribe((data) => {
            this._loadFileSubscription = null;

            for(let _url of urlList) {
                let _wasFound = false;

                for(let _cData of data) {
                    if(_cData.url == _url) {
                        this.alreadyLoaded.push(new PgFile(_cData))
                        _wasFound = true;
                        break;
                    }
                }

                if(!_wasFound) {
                    let _fileName = this._getFileNameFromUrl(_url)

                    if(_fileName != null) {
                        this.alreadyLoaded.push(new PgFile({
                            nome_file: _fileName,
                            url: _url,
                            nome: _fileName.replace(/\.[^\.]*$/, '')
                        }))
                    }
                }
            }

            this.isLoading = false;

            if(andTriggerChange) {
                this._finishSave()
            }
        })
    }

    isSaving = false;

    saveFiles() {
        this.isSaving = true;

        let _reqNum = 0;

        for(let _cLoaded of this.alreadyLoaded) {
            if(_cLoaded.deleted || !this.multi) {
                _reqNum++;

                //NB: non cancello mai i file, li rimuovo solo dai riferimenti
                //this.dataService.deleteElement('File', _cLoaded.id).subscribe((data) => {
                setTimeout(() => {
                    this.alreadyLoaded.splice(this.alreadyLoaded.indexOf(_cLoaded), 1)

                    _reqNum--;
                    if(_reqNum <= 0) {
                        this._finishSave();
                    }
                })
                //})
            }
        }

        if(this.selectedFiles != null) {
            for(let i = this.selectedFiles.length - 1; i >= 0; i--) {
                if(this.selectedFiles[i].error != null) {
                    this.selectedFiles.splice(i, 1);
                }
            }

            for(let _cSelected of this.selectedFiles) {
                _reqNum++; 
                this.dataService.postFile('File', _cSelected.file || _cSelected.external, { group_id: this.groupId, realm_id: this.forceRealm || this.realmId, directory_id: this.directoryId }, true).subscribe((progress) => {
                    _cSelected.progress = progress.progress;
    
                    if(progress.error) {
                        if(progress.error.status == 422) {
                            _cSelected.error = 'Format not supported';
                        }
                        else {
                            _cSelected.error = 'Upload failed';
                        }
                        
                        _reqNum--;
                    }
                    else if(progress.complete) {
                        this.alreadyLoaded.push(new PgFile(progress.data));
                        this.selectedFiles.splice(this.selectedFiles.indexOf(_cSelected), 1)
                        _reqNum--;
                    }
    
                    if(_reqNum <= 0) {
                        this._finishSave();
                    }
                })
            }
        }
    }

    private _finishSave() {
        this.isSaving = false;

        let _cVal = null;
        this.resourcePickerSelection = [];

        for(let _cFile of this.alreadyLoaded) {
            this.resourcePickerSelection.push(_cFile.url);

            if(this.multi) {
                if(_cVal == null) _cVal = [];
                _cVal.push(_cFile.url);
            }
            else if(_cVal == null) {
                _cVal = _cFile.url;
            }
        }

        if(this._onChange != null) {
            if(this.multi) {
                this._onChange(_cVal == null ? _cVal : JSON.stringify(_cVal));
            }
            else {
                this._onChange(_cVal);
            }
        }
    }

    _onChange;

    registerOnChange(fn: any) {
        this._onChange = fn;
    }

    _onTouched;

    registerOnTouched(fn: any) {
        this._onTouched = fn;
    }

    // INTERFACCIA Validator

    validate()  {
        return (!this.required || this.value != null)  ? null : {
            required: {
                valid: false
            }
        }
    };
}
